import { Helmet } from "react-helmet-async";
import { Phone } from "lucide-react";
import customerCareIcon from "@/assets/customer-care-icon.png";
import inspectionHeroBg from "@/assets/inspection-hero-bg.png";
import inspectionSection from "@/assets/inspection-section.jpg";

const ServiceInspection = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Inspection du système de traite - Agroref</title>
        <meta name="description" content="Inspection complète de votre système de traite pour garantir performance et qualité du lait" />
        <link rel="preload" as="image" href={inspectionHeroBg} />
      </Helmet>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-12 md:py-20 min-h-[600px] flex items-center overflow-hidden">
          <img src={inspectionHeroBg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="sync" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl bg-black/75 backdrop-blur-md rounded-lg p-8 md:p-12">
              <div className="w-16 h-1 bg-red-600 mb-6" />
              <h1 className="text-3xl md:text-4xl md:text-5xl font-bold text-white mb-6">
                Inspection du système de traite: Votre garantie de qualité
              </h1>
              <p className="text-lg text-white/80 mb-6">
                Est-ce que votre système de traite fonctionne à son meilleur? C&apos;est la question que notre service d&apos;inspection du système de traite vous aide à répondre. Détectez les problèmes avant qu&apos;ils ne nuisent à la qualité de votre lait et à la santé de vos vaches.
              </p>
              <p className="text-lg text-white/80 mb-8">
                Avec nos experts qualifiés, nous analysons en profondeur votre système de traite et vous fournissons un rapport détaillé. Ainsi, vous savez précisément où faire des réparations ou des améliorations. Améliorez votre production de lait avec notre service d&apos;inspection du système de traite.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Programmez votre inspection maintenant
              </a>
            </div>
          </div>
        </section>

        {/* Section: Pourquoi inspecter */}
        <section className="py-12 md:py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Pourquoi est-il essentiel d&apos;inspecter votre système de traite?</h2>
              
              <div className="grid md:grid-cols-2 gap-8 text-muted-foreground">
                <div className="space-y-6">
                  <p>
                    La traite est un pivot crucial dans la production laitière. Son efficacité s&apos;appuie grandement sur la performance de votre système de traite. Toutefois, une défaillance ou un dérèglement dans ce système peut rapidement saper la qualité de votre lait. En outre, ces problèmes peuvent nuire à la santé de vos vaches et empiéter sur votre productivité.
                  </p>
                  
                  <p>
                    L&apos;importance de l&apos;inspection du système de traite ne saurait être surestimée dans ce contexte. Cette vérification offre une occasion précieuse d&apos;identifier et de rectifier les anomalies avant qu&apos;elles n&apos;entraînent de sérieuses complications. L&apos;examen minutieux de chaque composant – des liners aux pulsateurs, en passant par les trayons et les réservoirs de lait – est indispensable pour assurer un fonctionnement sans faille.
                  </p>
                </div>

                <div className="space-y-6">
                  <p>
                    L&apos;inspection régulière du système de traite offre une multitude d&apos;avantages. Elle assure un <strong>fonctionnement optimal</strong> du système de traite, veille à la santé des vaches, et se traduit par une production laitière de haute qualité. Au-delà de sa nature préventive, cette pratique constitue un investissement essentiel pour l&apos;efficacité de votre exploitation.
                  </p>
                  
                  <p>
                    En somme, l&apos;inspection du système de traite est essentielle pour le succès d&apos;une exploitation laitière. En anticipant les problèmes, elle protège la continuité de votre production. Plus qu&apos;une maintenance, c&apos;est une <strong>assurance pour une production laitière saine et rentable</strong>, un véritable investissement pour la durabilité de votre exploitation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Notre expertise */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Faites confiance à notre expertise en inspection du système de traite</h2>
              
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    Chez Agro, nous sommes dévoués à vous aider à maintenir la fiabilité et l&apos;efficacité de votre système de traite. Notre service d&apos;inspection ne laisse aucun détail au hasard. Nous mobilisons une équipe d&apos;experts qualifiés et chevronnés pour effectuer une évaluation approfondie, en utilisant les technologies les plus sophistiquées.
                  </p>
                  
                  <p>
                    Notre évaluation englobe tous les composants de votre système de traite, des liners aux pulsateurs, en passant par les trayons, les griffes de traite, les conduites de lait et les réservoirs. Après l&apos;inspection, nous vous fournissons un <strong>rapport détaillé</strong> qui inclut les problèmes potentiels identifiés, ainsi que des recommandations sur les réparations nécessaires ou les améliorations à apporter.
                  </p>
                  
                  <p>
                    Opter pour notre service d&apos;inspection du système de traite, c&apos;est s&apos;assurer un partenariat avec des experts qui se soucient autant de la qualité de votre production laitière que vous. Nous mettons un point d&apos;honneur à vous fournir un <strong>service d&apos;inspection exceptionnel</strong> qui vous aidera à maintenir un système de traite performant et fiable.
                  </p>
                </div>

                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={inspectionSection}
                    alt="Inspection du système de traite"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-12 md:py-16 bg-[#333333] overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-xl font-semibold mb-6 text-white/80 uppercase tracking-wide">Contactez-nous</p>
            <h3 className="text-2xl font-bold mb-4 text-white">Prêt à Optimiser Votre Production Laitière?</h3>
            <p className="text-lg text-white/70 mb-4 max-w-3xl mx-auto">
              Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
            </p>
            <p className="text-base text-white/60 mb-8">
              Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <Phone className="h-5 w-5" />
              Contactez-nous dès maintenant!
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceInspection;
